// DOCUMENTS ZONE — the PDFs a profile run read and kept (BUILD-BRIEF-R3d §2.3, §4).
//
// A profile run that finds an annual report reads it through the scout's pdf job and KEEPS THE
// FILE. That file is the evidence under a dozen size_money cells, and a quote nobody can open is
// half a receipt: this zone is the other half. One row per kept document — the year, the source
// URL as a link, the fetch date, the size, the cells that cite it, and an "Open" that streams
// the stored PDF back from `GET /api/snapshots/:id/pdf`.
//
// THE RULES THIS FILE MUST NOT BREAK:
//   · A DOCUMENT IS NOT A CLAIM. The list is arithmetic over what the run kept — no ◇, no ▪.
//     What carries a mark is the cell that cites the document, and that mark is on the Profile
//     zone where the cell lives.
//   · "not collected" IS NOT ZERO. A report whose year nobody read renders `fv--unknown`; a
//     document no cell cites yet is a COUNTED zero and renders `fv--nil`. Different facts.
//   · A size and a date are QUANTITIES (`Fig`); a URL is an IDENTITY (`Ref`). A cell key is an
//     ADDRESS, not a ref anyone quotes: the cited cells read as their names, with the key on
//     hover, because mono here would dress machine plumbing as evidence.
//
// The zone takes the profile the Organisation page already loaded, so it costs no second call.
import { Fragment } from 'react';
import { Fig, Ref, Sep, Sheet, Tbl, Zone, ZoneHead } from './PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { labelFor, type RasterLang } from '../../shared/raster.v1.js';
import type { ProfileResponse } from '../../shared/types.js';
import './DocumentsZone.de.js';
import './DocumentsZone.fr.js';

// ── the contract, read defensively ──────────────────────────────────────────
//
// `documents` arrives on `GET /api/profile/:orgId` from the profile-v2 round. A console that has
// been deployed against a server that does not yet answer it must show "not collected", never a
// crash and never a zero — so the shape is narrowed at runtime here rather than trusted from the
// type.

/** One kept PDF on the record. `year` is the report's year, not the fetch year. */
export interface ProfileDocument {
  snapshot_id: number;
  url: string;
  year: number | null;
  fetched_at: string | null;
  byte_size: number | null;
  /** Full cell keys — `profile.size_money.fte_total` — of the cells whose evidence is this file. */
  cited_cells: string[];
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

/** The documents on a loaded profile, newest report first. `null` while nothing has answered. */
export function readDocuments(profile: ProfileResponse | null | undefined): ProfileDocument[] | null {
  if (!profile) return null;
  const raw = (profile as unknown as { documents?: unknown }).documents;
  if (!Array.isArray(raw)) return null;
  const docs: ProfileDocument[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const d = item as Record<string, unknown>;
    const id = num(d.snapshot_id);
    const url = str(d.url);
    if (id === null || url === null) continue;
    docs.push({
      snapshot_id: id,
      url,
      year: num(d.year),
      fetched_at: str(d.fetched_at),
      byte_size: num(d.byte_size),
      cited_cells: Array.isArray(d.cited_cells) ? d.cited_cells.filter((c): c is string => typeof c === 'string') : [],
    });
  }
  docs.sort((a, b) => (b.year ?? 0) - (a.year ?? 0) || (b.fetched_at ?? '').localeCompare(a.fetched_at ?? ''));
  return docs;
}

/** How many cells a fill may propose — the "n of 50 cells" denominator of §2.6. `null` while a
 *  server that predates the receipt has not answered it; the caller then falls back to what it
 *  can count itself rather than printing a figure nobody sent. */
export function readProposable(profile: ProfileResponse | null | undefined): number | null {
  if (!profile) return null;
  return num((profile as unknown as { proposable?: unknown }).proposable);
}

/** THE FIGURE VOICE USES THE TYPOGRAPHIC APOSTROPHE — `39’861`, never `39'861`.
 *  `Intl.NumberFormat('de-CH')` groups with U+0027 in some browsers' ICU and U+2019 in others;
 *  a record that spells the same number two ways depending on the browser is not a record. */
export function swissTicks(s: string): string {
  return s.replace(/'/g, '’');
}

/** `profile.size_money.fte_total` → the cell's name in the console's language. */
export function cellLabel(lang: RasterLang, key: string): string {
  const parts = key.split('.');
  if (parts.length === 3 && parts[0] === 'profile') return labelFor(lang, parts[1], parts[2]);
  if (parts.length === 2) return labelFor(lang, parts[0], parts[1]);
  return key;
}

/** The stored bytes, streamed back through the app. Never the third party's URL. */
export function documentHref(snapshotId: number): string {
  return `/api/snapshots/${snapshotId}/pdf`;
}

/** A URL short enough to read in a table cell, with the whole thing on hover. */
function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname;
    const whole = `${u.host}${path}`;
    return whole.length > 52 ? `${whole.slice(0, 24)}…${whole.slice(-24)}` : whole;
  } catch {
    return url.length > 52 ? `${url.slice(0, 24)}…${url.slice(-24)}` : url;
  }
}

// ── the zone ────────────────────────────────────────────────────────────────

export interface DocumentsZoneProps {
  /** The profile the page already loaded. `null` while the Profile zone is still asking. */
  profile: ProfileResponse | null;
}

export default function DocumentsZone({ profile }: DocumentsZoneProps) {
  const t = useT();
  const { lang, fmtDate, fmtNum } = useLocale();
  const docs = readDocuments(profile);

  return (
    <Zone id="documents">
      <ZoneHead
        label={t('Documents')}
        /* A count of kept files is arithmetic — it carries no claim mark. */
        count={docs ? <Fig>{docs.length}</Fig> : undefined}
        note={t('the PDFs a profile run read and kept, with the cells that rest on them')}
      />
      {!profile ? (
        <p className="zone-empty">{t('Loading…')}</p>
      ) : docs === null ? (
        <p className="zone-empty">
          {t('This server does not report kept documents yet. A profile run keeps the annual report it reads; once the record answers with its documents they are listed here.')}
        </p>
      ) : docs.length === 0 ? (
        <p className="zone-empty">
          {t('No document is kept for this organisation. A profile fill that finds an annual report reads the PDF in the same run and keeps it here, beside the cells it filled.')}
        </p>
      ) : (
        <Sheet clip>
          <Tbl>
            <thead>
              <tr>
                <th className="num">{t('Year')}</th>
                <th>{t('Source')}</th>
                <th className="num">{t('Fetched')}</th>
                <th className="num">{t('Size')}</th>
                <th>{t('Cited by')}</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((doc) => (
                <tr key={doc.snapshot_id}>
                  <td className="num">
                    {doc.year !== null
                      ? <Fig>{doc.year}</Fig>
                      : <span className="fv--unknown">{t('not collected')}</span>}
                  </td>
                  <td>
                    <a className="lnk" href={doc.url} target="_blank" rel="noreferrer noopener" title={doc.url}>
                      <Ref>{shortUrl(doc.url)}</Ref>
                    </a>
                    {' '}<Sep />{' '}
                    <a
                      className="lnk"
                      href={documentHref(doc.snapshot_id)}
                      target="_blank"
                      rel="noreferrer noopener"
                      title={t('Open the kept PDF as the run read it')}
                    >
                      {t('Open')}
                    </a>
                  </td>
                  <td className="num">
                    {doc.fetched_at
                      ? <Fig>{fmtDate(doc.fetched_at)}</Fig>
                      : <span className="fv--unknown">{t('not collected')}</span>}
                  </td>
                  <td className="num">
                    {doc.byte_size !== null
                      ? <Fig unit=" kB" title={t('{n} bytes', { n: swissTicks(fmtNum(doc.byte_size)) })}>{swissTicks(fmtNum(Math.max(1, Math.round(doc.byte_size / 1024))))}</Fig>
                      : <span className="fv--unknown">{t('not collected')}</span>}
                  </td>
                  <td>
                    {doc.cited_cells.length === 0 ? (
                      /* Counted, and the count is zero: the file is kept, no cell rests on it yet. */
                      <span className="fv--nil">{t('no cell cites it')}</span>
                    ) : (
                      /* THE OPERATOR READS THE CELL'S NAME, not its key. `profile.decision.auditor`
                         is a machine address, and mono is the voice of an identity someone quotes
                         — a reader never quotes a cell key. The key stays on hover, for the one
                         moment it is wanted. */
                      doc.cited_cells.map((key, i) => (
                        <Fragment key={key}>
                          {i > 0 && <>{' '}<Sep />{' '}</>}
                          <span title={key}>{cellLabel(lang as RasterLang, key)}</span>
                        </Fragment>
                      ))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Tbl>
        </Sheet>
      )}
    </Zone>
  );
}
